import { z } from "@hono/zod-openapi";

import type { TokenUsage } from "./tokens";

/**
 * A model's price card in USD per million tokens. The same shape is a
 * deployment/workspace route `pricing` override, which replaces the catalog
 * card when that route serves the call (Bedrock cross-region premiums, Azure
 * rates, aggregator markup) — so the field names are persisted config.
 */
export const ModelPricingSchema = z.object({
  inputCostPerMillionTokens: z.number().nonnegative(),
  outputCostPerMillionTokens: z.number().nonnegative(),
  cacheWriteCostPerMillionTokens: z.number().nonnegative().optional(),
  cacheReadCostPerMillionTokens: z.number().nonnegative().optional(),
});

export type ModelPricing = {
  inputCostPerMillionTokens: number;
  outputCostPerMillionTokens: number;
  cacheWriteCostPerMillionTokens?: number | undefined;
  cacheReadCostPerMillionTokens?: number | undefined;
};

/**
 * Catalog price cards, keyed by the canonical `AGENT_MODEL_REGISTRY` id. Look
 * one up with `modelPricing(modelId)` from `@quovy/types`, which applies the
 * registry's alias normalization first; a registry test pins the two key sets
 * together.
 *
 * Launch/intro discounts are ignored on purpose: sticker rates keep accounting
 * correct once a promo lapses (gpt-5.6-sol's $4/$20 through 2026-11-21;
 * gemini-3.7-flash's $0.75/$3.75 and gemini-3.8-flash's 50% off through
 * 2026-12-31; gemini-4-argon's $2/$10 through 2026-11-30). Gemini Pro and xAI
 * rates are the <200k-prompt tier and claude-haiku-5-5's the <=100k-prompt
 * tier; long-context surcharges are not modelled. The 2026-07-21 Gemini Flash
 * launch published input/output only; cache-read is the standard
 * 10%-of-input Flash discount.
 */
export const MODEL_PRICES = {
  "claude-fable-5": {
    inputCostPerMillionTokens: 10,
    outputCostPerMillionTokens: 50,
    cacheWriteCostPerMillionTokens: 12.5,
    cacheReadCostPerMillionTokens: 1,
  },
  "claude-haiku-4-5": {
    inputCostPerMillionTokens: 1,
    outputCostPerMillionTokens: 5,
    // Anthropic prompt caching: writes cost 1.25x input, cache hits cost 0.1x input
    cacheWriteCostPerMillionTokens: 1.25,
    cacheReadCostPerMillionTokens: 0.1,
  },
  "claude-haiku-5-5": {
    inputCostPerMillionTokens: 0.1,
    outputCostPerMillionTokens: 0.5,
    cacheWriteCostPerMillionTokens: 0.125,
    cacheReadCostPerMillionTokens: 0.01,
  },
  "claude-opus-4-6": {
    inputCostPerMillionTokens: 5,
    outputCostPerMillionTokens: 25,
    cacheWriteCostPerMillionTokens: 6.25,
    cacheReadCostPerMillionTokens: 0.5,
  },
  "claude-opus-4-7": {
    inputCostPerMillionTokens: 5,
    outputCostPerMillionTokens: 25,
    cacheWriteCostPerMillionTokens: 6.25,
    cacheReadCostPerMillionTokens: 0.5,
  },
  "claude-opus-4-8": {
    inputCostPerMillionTokens: 5,
    outputCostPerMillionTokens: 25,
    cacheWriteCostPerMillionTokens: 6.25,
    cacheReadCostPerMillionTokens: 0.5,
  },
  "claude-opus-5": {
    inputCostPerMillionTokens: 5,
    outputCostPerMillionTokens: 25,
    cacheWriteCostPerMillionTokens: 6.25,
    cacheReadCostPerMillionTokens: 0.5,
  },
  "claude-opus-5-5": {
    inputCostPerMillionTokens: 4,
    outputCostPerMillionTokens: 20,
    cacheWriteCostPerMillionTokens: 5,
    cacheReadCostPerMillionTokens: 0.2,
  },
  "claude-sonnet-4-6": {
    inputCostPerMillionTokens: 3,
    outputCostPerMillionTokens: 15,
    cacheWriteCostPerMillionTokens: 3.75,
    cacheReadCostPerMillionTokens: 0.3,
  },
  // Anthropic made the $2/$10 launch price standard; the planned 2026-09-01 rise to $3/$15 never happened.
  "claude-sonnet-5": {
    inputCostPerMillionTokens: 2,
    outputCostPerMillionTokens: 10,
    cacheWriteCostPerMillionTokens: 2.5,
    cacheReadCostPerMillionTokens: 0.2,
  },
  "claude-sonnet-5-5": {
    inputCostPerMillionTokens: 2,
    outputCostPerMillionTokens: 10,
    cacheWriteCostPerMillionTokens: 2.5,
    cacheReadCostPerMillionTokens: 0.1,
  },
  "gpt-4o": {
    inputCostPerMillionTokens: 2.5,
    outputCostPerMillionTokens: 10,
    cacheReadCostPerMillionTokens: 1.25,
  },
  "gpt-4o-mini": {
    inputCostPerMillionTokens: 0.15,
    outputCostPerMillionTokens: 0.6,
    cacheReadCostPerMillionTokens: 0.075,
  },
  "gpt-5": {
    inputCostPerMillionTokens: 1.25,
    outputCostPerMillionTokens: 10,
    cacheReadCostPerMillionTokens: 0.125,
  },
  "gpt-5-mini": {
    inputCostPerMillionTokens: 0.25,
    outputCostPerMillionTokens: 2,
    cacheReadCostPerMillionTokens: 0.025,
  },
  "gpt-5.4": {
    inputCostPerMillionTokens: 2.5,
    outputCostPerMillionTokens: 15,
    cacheReadCostPerMillionTokens: 0.25,
  },
  "gpt-5.4-mini": {
    inputCostPerMillionTokens: 0.75,
    outputCostPerMillionTokens: 4.5,
    cacheReadCostPerMillionTokens: 0.075,
  },
  "gpt-5.4-nano": {
    inputCostPerMillionTokens: 0.2,
    outputCostPerMillionTokens: 1.25,
    cacheReadCostPerMillionTokens: 0.02,
  },
  "gpt-5.4-pro": {
    inputCostPerMillionTokens: 30,
    outputCostPerMillionTokens: 180,
    cacheReadCostPerMillionTokens: 30,
  },
  "gpt-5.5": {
    inputCostPerMillionTokens: 5,
    outputCostPerMillionTokens: 30,
    cacheReadCostPerMillionTokens: 0.5,
  },
  "gpt-5.5-pro": {
    inputCostPerMillionTokens: 30,
    outputCostPerMillionTokens: 180,
    cacheReadCostPerMillionTokens: 30,
  },
  // Sticker rate; the $4/$20 promotion through at least 2026-11-21 is excluded.
  "gpt-5.6-sol": {
    inputCostPerMillionTokens: 5,
    outputCostPerMillionTokens: 30,
    cacheWriteCostPerMillionTokens: 6.25,
    cacheReadCostPerMillionTokens: 0.5,
  },
  "gpt-5.6-terra": {
    inputCostPerMillionTokens: 2,
    outputCostPerMillionTokens: 12,
    cacheWriteCostPerMillionTokens: 2.5,
    cacheReadCostPerMillionTokens: 0.2,
  },
  "gpt-5.6-luna": {
    inputCostPerMillionTokens: 0.2,
    outputCostPerMillionTokens: 1.2,
    cacheWriteCostPerMillionTokens: 0.25,
    cacheReadCostPerMillionTokens: 0.02,
  },
  "gpt-5-nano": {
    inputCostPerMillionTokens: 0.05,
    outputCostPerMillionTokens: 0.4,
    cacheReadCostPerMillionTokens: 0.005,
  },
  "gpt-6-astra": {
    inputCostPerMillionTokens: 10,
    outputCostPerMillionTokens: 50,
    cacheWriteCostPerMillionTokens: 12.5,
    cacheReadCostPerMillionTokens: 1,
  },
  "gpt-6-sol": {
    inputCostPerMillionTokens: 2,
    outputCostPerMillionTokens: 10,
    cacheWriteCostPerMillionTokens: 2.5,
    cacheReadCostPerMillionTokens: 0.2,
  },
  "gpt-6-luna": {
    inputCostPerMillionTokens: 0.1,
    outputCostPerMillionTokens: 0.5,
    cacheWriteCostPerMillionTokens: 0.125,
    cacheReadCostPerMillionTokens: 0.01,
  },
  "gpt-6.1-sol": {
    inputCostPerMillionTokens: 2,
    outputCostPerMillionTokens: 10,
    cacheWriteCostPerMillionTokens: 2.5,
    cacheReadCostPerMillionTokens: 0.1,
  },
  "gpt-6.1-terra": {
    inputCostPerMillionTokens: 1,
    outputCostPerMillionTokens: 5,
    cacheWriteCostPerMillionTokens: 1.25,
    cacheReadCostPerMillionTokens: 0.05,
  },
  // Retired ids xAI now bills at the model that serves them: grok-4.3 for
  // grok-4 and grok-4-fast, grok-build-0.1 for grok-code-fast-1.
  "grok-4": {
    inputCostPerMillionTokens: 1.25,
    outputCostPerMillionTokens: 2.5,
    cacheReadCostPerMillionTokens: 0.2,
  },
  "grok-4-fast": {
    inputCostPerMillionTokens: 1.25,
    outputCostPerMillionTokens: 2.5,
    cacheReadCostPerMillionTokens: 0.2,
  },
  "grok-4.3": {
    inputCostPerMillionTokens: 1.25,
    outputCostPerMillionTokens: 2.5,
    cacheReadCostPerMillionTokens: 0.2,
  },
  "grok-4.5": {
    inputCostPerMillionTokens: 2,
    outputCostPerMillionTokens: 6,
    cacheReadCostPerMillionTokens: 0.3,
  },
  "grok-4.6": {
    inputCostPerMillionTokens: 2,
    outputCostPerMillionTokens: 6,
    cacheReadCostPerMillionTokens: 0.5,
  },
  "grok-4.7": {
    inputCostPerMillionTokens: 2,
    outputCostPerMillionTokens: 6,
    cacheReadCostPerMillionTokens: 0.5,
  },
  "grok-build-0.1": {
    inputCostPerMillionTokens: 1,
    outputCostPerMillionTokens: 2,
    cacheReadCostPerMillionTokens: 0.2,
  },
  "grok-code-fast-1": {
    inputCostPerMillionTokens: 1,
    outputCostPerMillionTokens: 2,
    cacheReadCostPerMillionTokens: 0.2,
  },
  "gemini-3-flash-preview": {
    inputCostPerMillionTokens: 0.5,
    outputCostPerMillionTokens: 3,
    cacheReadCostPerMillionTokens: 0.05,
  },
  "gemini-3.1-flash-lite": {
    inputCostPerMillionTokens: 0.25,
    outputCostPerMillionTokens: 1.5,
    cacheReadCostPerMillionTokens: 0.025,
  },
  "gemini-3.1-pro-preview": {
    inputCostPerMillionTokens: 2,
    outputCostPerMillionTokens: 12,
    cacheReadCostPerMillionTokens: 0.2,
  },
  "gemini-3.1-pro-preview-customtools": {
    inputCostPerMillionTokens: 2,
    outputCostPerMillionTokens: 12,
    cacheReadCostPerMillionTokens: 0.2,
  },
  "gemini-3.5-flash": {
    inputCostPerMillionTokens: 1.5,
    outputCostPerMillionTokens: 9,
    cacheReadCostPerMillionTokens: 0.15,
  },
  "gemini-3.5-flash-lite": {
    inputCostPerMillionTokens: 0.3,
    outputCostPerMillionTokens: 2.5,
    cacheReadCostPerMillionTokens: 0.03,
  },
  "gemini-3.6-flash": {
    inputCostPerMillionTokens: 1.5,
    outputCostPerMillionTokens: 7.5,
    cacheReadCostPerMillionTokens: 0.15,
  },
  "gemini-3.7-flash": {
    inputCostPerMillionTokens: 1.5,
    outputCostPerMillionTokens: 7.5,
    cacheReadCostPerMillionTokens: 0.15,
  },
  "gemini-3.8-flash": {
    inputCostPerMillionTokens: 1.5,
    outputCostPerMillionTokens: 7.5,
    cacheReadCostPerMillionTokens: 0.15,
  },
  "gemini-4-argon": {
    inputCostPerMillionTokens: 4,
    outputCostPerMillionTokens: 20,
    cacheReadCostPerMillionTokens: 0.2,
  },
} as const satisfies Record<string, ModelPricing>;

/**
 * Compute the USD cost of a single set of token counts against a price card.
 *
 * `usage.input` and `usage.output` are INCLUSIVE TOTALS, matching what the AI
 * SDK reports and what every call site in this repo passes:
 *
 *   • `input`  = uncached input + `cacheRead` + `cacheWrite`. Every provider
 *     folds the cache counts into the headline input number — Anthropic sums
 *     them explicitly in `convertAnthropicUsage`, OpenAI's `prompt_tokens`
 *     includes `cached_tokens`, Gemini's `total_input_tokens` includes the
 *     cached prefix — and `inputTokenDetails.cacheRead/cacheWrite` are the
 *     BREAKDOWN of that total, not extra tokens beside it.
 *   • `output` = text + `reasoning`. Same story: Gemini's output total is
 *     `candidates + thoughts`, OpenAI's `completion_tokens` includes
 *     `reasoning_tokens`. (Anthropic reports no reasoning split at all, so
 *     thinking tokens simply stay inside `output`.)
 *
 * So each tier is priced on its own rate ONCE: we subtract the detail counts
 * back out of the totals rather than adding them on top. Billing the totals at
 * the input rate and then adding `cacheRead` again charged cache hits at 1.1×
 * the input rate instead of 0.1× — an ~11× overcharge on the cached portion.
 *
 * Returns a fixed-precision string (6 decimals) so downstream callers can
 * persist to text columns without losing precision to JSON floats. A null
 * card (unknown model) returns "0" — callers should treat that as "unpriced"
 * rather than free, and upstream logging should flag it.
 */
export function calculateTokenCostUsd(
  pricing: ModelPricing | null | undefined,
  usage: TokenUsage,
): string {
  if (!pricing) return "0";

  const perMillion = 1_000_000;

  const cacheRead = Math.max(usage.cacheRead ?? 0, 0);
  const cacheWrite = Math.max(usage.cacheWrite ?? 0, 0);
  const reasoning = Math.max(usage.reasoning ?? 0, 0);

  // Clamped: a provider that reports the detail counts OUTSIDE the total would
  // otherwise drive these negative and credit the run.
  const uncachedInput = Math.max(usage.input - cacheRead - cacheWrite, 0);
  const textOutput = Math.max(usage.output - reasoning, 0);

  const { cacheReadRate, cacheWriteRate } = resolveCacheRates(pricing);

  const inputCost = (uncachedInput * pricing.inputCostPerMillionTokens) / perMillion;
  const cacheReadCost = (cacheRead * cacheReadRate) / perMillion;
  const cacheWriteCost = (cacheWrite * cacheWriteRate) / perMillion;
  const outputCost = (textOutput * pricing.outputCostPerMillionTokens) / perMillion;
  // Reasoning bills at the output rate on Anthropic and OpenAI o-series.
  const reasoningCost = (reasoning * pricing.outputCostPerMillionTokens) / perMillion;

  const total = inputCost + outputCost + reasoningCost + cacheReadCost + cacheWriteCost;
  return total.toFixed(6);
}

/**
 * A card's cache rates per million tokens; a card that omits them (every
 * openai/xai entry) bills cache reads at 10% and writes at 125% of input.
 */
export function resolveCacheRates(
  pricing: ModelPricing,
  provider?: string,
  catalogPricing: ModelPricing = pricing,
): {
  cacheReadRate: number;
  cacheWriteRate: number;
} {
  const defaults =
    CACHE_RATE_MULTIPLIERS_BY_PROVIDER[provider ?? ""] ?? UNKNOWN_PROVIDER_CACHE_RATE_MULTIPLIERS;
  const catalogInput = catalogPricing.inputCostPerMillionTokens;
  const multiplier = (catalogRate: number | undefined, fallback: number) =>
    catalogRate !== undefined && catalogInput > 0 ? catalogRate / catalogInput : fallback;
  return {
    cacheReadRate:
      pricing.cacheReadCostPerMillionTokens ??
      pricing.inputCostPerMillionTokens *
        multiplier(catalogPricing.cacheReadCostPerMillionTokens, defaults.read),
    cacheWriteRate:
      pricing.cacheWriteCostPerMillionTokens ??
      pricing.inputCostPerMillionTokens *
        multiplier(catalogPricing.cacheWriteCostPerMillionTokens, defaults.write),
  };
}

const CACHE_RATE_MULTIPLIERS_BY_PROVIDER: Record<string, { read: number; write: number }> = {
  anthropic: { read: 0.1, write: 1.25 },
  openai: { read: 0.1, write: 1 },
  google: { read: 0.1, write: 1 },
  xai: { read: 0.25, write: 1 },
};
const UNKNOWN_PROVIDER_CACHE_RATE_MULTIPLIERS = { read: 0.1, write: 1 };

/** Resolve both cache tiers before persisting a route-specific price card. */
export function withResolvedCacheRates(
  pricing: ModelPricing,
  catalog: { provider: string; pricing: ModelPricing } | null,
): ModelPricing {
  const { cacheReadRate, cacheWriteRate } = resolveCacheRates(
    pricing,
    catalog?.provider,
    catalog?.pricing,
  );
  return {
    ...pricing,
    cacheReadCostPerMillionTokens: cacheReadRate,
    cacheWriteCostPerMillionTokens: cacheWriteRate,
  };
}

/** Token counts paired with the card they bill against; null card = unpriced model. */
export type PricedTokenUsage = {
  pricing: ModelPricing | null | undefined;
  usage: TokenUsage;
};

/**
 * Cost breakdown in USD for a processor run. Strings preserve precision when
 * persisting; `totalCostUsd` is primary + subagents and is the canonical
 * `cost_usd` for the run.
 */
export type RunCostBreakdown = {
  primaryCostUsd: string;
  subagentCostUsd: string;
  totalCostUsd: string;
};

/**
 * Primary and subagent tiers are priced on their own cards — extractor runs
 * typically pair a Sonnet/Opus orchestrator with a Haiku subagent.
 */
export function calculateRunCostBreakdown(input: {
  primary: PricedTokenUsage;
  subagents: PricedTokenUsage | PricedTokenUsage[];
}): RunCostBreakdown {
  const primaryCostUsd = calculateTokenCostUsd(input.primary.pricing, input.primary.usage);
  const subagentCostUsd = Array.isArray(input.subagents)
    ? addUsdAmounts(
        ...input.subagents.map((entry) => calculateTokenCostUsd(entry.pricing, entry.usage)),
      )
    : calculateTokenCostUsd(input.subagents.pricing, input.subagents.usage);
  const totalCostUsd = addUsdAmounts(primaryCostUsd, subagentCostUsd);
  return { primaryCostUsd, subagentCostUsd, totalCostUsd };
}

/** Sum persisted six-decimal USD strings; null/undefined count as zero. */
export function addUsdAmounts(...amounts: Array<string | null | undefined>): string {
  return amounts.reduce((total, amount) => total + Number.parseFloat(amount ?? "0"), 0).toFixed(6);
}

/** Sort comparator for USD decimal strings, cheapest first. */
export function compareUsd(a: string | null | undefined, b: string | null | undefined): number {
  return Number.parseFloat(a ?? "0") - Number.parseFloat(b ?? "0");
}

/** Integer cents (e.g. `agent_eval_results.cost_cents`) from a USD decimal; null when unknown. */
export function usdToCents(amountUsd: string | number | null | undefined): number | null {
  if (amountUsd == null) return null;
  const dollars = typeof amountUsd === "string" ? Number.parseFloat(amountUsd) : amountUsd;
  return Number.isFinite(dollars) ? Math.round(dollars * 100) : null;
}

export function centsToUsdString(cents: number): string {
  return (cents / 100).toFixed(6);
}
